/**
 * Wires `reporting.model.ts` to the two reads Costs & Income needs — OND-97.
 *
 * TWO READS AND NO WRITES. `fetchRegistrationFees` and
 * `fetchGallerySettlements` are the same routes the Transaction Centre and the
 * settlements ledger already use, so this screen adds no endpoint and no
 * migration: both ledgers are already answered by `ondo.gallery_registration_fees()`
 * and `ondo.gallery_settlements()` under the caller's own token, and the
 * policies on them are the access control this screen applies. There is
 * deliberately no write of any kind here — recording a fee as paid stays on the
 * Transaction Centre behind the `admin` rung, because a reporting view does not
 * grant permission to mark a payment.
 *
 * BOTH READS ARE THE SUBJECT, which is the one way this differs from
 * `useTransactionsController`. There, two of three reads are counts and swallow
 * their failure. Here every figure is a comparison of the two ledgers, so a
 * failure on either is the screen's error: a cost drawn with no income beside
 * it would be a report of a net position that was never computed.
 *
 * The state the model cannot hold is the period, the typed custom range, the
 * chosen currency, the sort, whether the voided rows are disclosed, and the
 * export's own in-flight flag.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { RegistrationFee, Settlement } from "@ondo/shared";

import { fetchGallerySettlements, fetchRegistrationFees } from "../lib/api.ts";
import { REPORTING_COPY } from "./reporting.constants.ts";
import type { ReportingPeriodKey, ReportingSortKey } from "./reporting.constants.ts";
import {
  describeReportingError,
  reportingFilename,
  toReportingCsv,
  toReportingView,
} from "./reporting.model.ts";
import type { CustomRange, ReportingView } from "./reporting.model.ts";

/**
 * U+FEFF, written as an escape and named.
 *
 * Not decoration: without it Excel reads a UTF-8 CSV as the local code page and
 * turns '£' into two characters, on a file whose whole subject is money. As an
 * escape rather than the character itself because the character is invisible in
 * every editor and `no-irregular-whitespace` is right to refuse it.
 */
const BYTE_ORDER_MARK = "\u{FEFF}";

export interface ReportingController {
  view: ReportingView;
  periodKey: ReportingPeriodKey;
  choosePeriod: (key: ReportingPeriodKey) => void;
  /** The two typed dates, as 'YYYY-MM-DD'. Only read when the period is custom. */
  custom: CustomRange;
  typeCustomFrom: (from: string) => void;
  typeCustomTo: (to: string) => void;
  chooseCurrency: (currency: string) => void;
  sort: ReportingSortKey;
  chooseSort: (sort: ReportingSortKey) => void;
  showVoid: boolean;
  toggleVoid: () => void;
  /** Builds the file from the view already on screen and hands it to the browser. */
  exportCsv: () => void;
  exporting: boolean;
  exportProblem: string | null;
}

/**
 * Costs & Income.
 *
 * `now` is read ONCE, on mount, and held. Every period resolves against it, so
 * a re-render cannot move a boundary under a reader who is part-way through
 * reading a total — and the figures on screen cannot change because a clock
 * ticked past midnight while they looked at them. `consumeIntendedRole`'s
 * arrangement, for the same reason: a value read once on mount is a value a
 * later render cannot re-apply differently.
 *
 * @param workBase '/gallery/ONDO-6624/work' — where a row links to.
 */
export function useReportingController(workBase: string): ReportingController {
  const [fees, setFees] = useState<RegistrationFee[] | null>(null);
  const [settlements, setSettlements] = useState<Settlement[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [periodKey, setPeriodKey] = useState<ReportingPeriodKey>("this-year");
  const [custom, setCustom] = useState<CustomRange>({ from: "", to: "" });
  const [currency, setCurrency] = useState<string | null>(null);
  const [sort, setSort] = useState<ReportingSortKey>("date");
  const [showVoid, setShowVoid] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProblem, setExportProblem] = useState<string | null>(null);

  const [now] = useState(() => new Date());

  useEffect(() => {
    let active = true;

    fetchRegistrationFees()
      .then((found) => {
        if (active) setFees(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeReportingError(cause));
      });

    fetchGallerySettlements()
      .then((found) => {
        if (active) setSettlements(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeReportingError(cause));
      });

    return () => {
      active = false;
    };
  }, []);

  const view = useMemo(
    () =>
      toReportingView({
        fees,
        settlements,
        error,
        workBase,
        periodKey,
        custom,
        currency,
        sort,
        now,
      }),
    [fees, settlements, error, workBase, periodKey, custom, currency, sort, now],
  );

  /**
   * The file, built from the view that is already on screen.
   *
   * THE SAME OBJECT THE READER IS LOOKING AT, which is how "the export applies
   * the same calculation logic as the screen" is made true rather than
   * asserted — there is one `toReportingView` and the CSV is a second rendering
   * of its output. It carries every row in the period and currency, including
   * the voided ones whether or not the disclosure is open, because what is
   * EXPORTED is the authorised filtered result and not the visible one.
   *
   * The download dance is `useContactsController`'s, and each step is there for
   * a reason it records: the anchor has to be in the document to be clickable,
   * and the object URL is revoked late rather than immediately because Safari
   * has not finished with it when `click()` returns.
   */
  const exportCsv = useCallback(() => {
    if (view.kind !== "ready" || !view.currency || !view.position) return;

    setExporting(true);
    setExportProblem(null);
    try {
      const text = toReportingCsv({
        period: view.period,
        currency: view.currency,
        position: view.position,
        rows: view.rows,
        voidRows: view.voidRows,
        exportedAt: new Date(),
      });
      // The byte-order mark is the one thing added to the model's text, and the
      // constant above says why.
      const blob = new Blob([`${BYTE_ORDER_MARK}${text}`], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = reportingFilename(view.period, view.currency);
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      setExportProblem(REPORTING_COPY.export.failed);
    } finally {
      setExporting(false);
    }
  }, [view]);

  return {
    view,
    periodKey,
    choosePeriod: setPeriodKey,
    custom,
    typeCustomFrom: (from: string) => setCustom((was) => ({ ...was, from })),
    typeCustomTo: (to: string) => setCustom((was) => ({ ...was, to })),
    chooseCurrency: setCurrency,
    sort,
    chooseSort: setSort,
    showVoid,
    toggleVoid: () => setShowVoid((was) => !was),
    exportCsv,
    exporting,
    exportProblem,
  };
}
